import { useState, useEffect, useCallback } from 'react';
import { sb } from '../../lib/supabase';
import { useSupabaseAuth } from '../../context/AuthContext';
import { DELIVERABLE_ROLE_LABEL } from '../../lib/gateHelpers';
import Loader from '../../components/Loader';
import Modal from '../../components/Modal';

const GATE_TYPES = [
  { id: 'lifecycle', label: 'Lifecycle' },
  { id: 'release', label: 'Release' },
  { id: 'go_live', label: 'Go-live' },
];

const BLANK = {
  name: '',
  description: '',
  gate_type: 'lifecycle',
  role: 'phase_output',
  order_index: 1,
  is_active: true,
};

function TemplateModal({ item, onSave, onClose, saving, error }) {
  const [form, setForm] = useState(item ? { ...item } : { ...BLANK });

  return (
    <Modal
      title={item ? 'Edit template' : 'New org template'}
      onClose={onClose}
      busy={saving}
      footer={(
        <>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-primary" disabled={saving || !form.name.trim()} onClick={() => onSave(form)}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    >
      <div className="form-row">
        <label className="form-label">Name *</label>
        <input className="form-input" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
      </div>
      <div className="form-row">
        <label className="form-label">Description</label>
        <textarea className="form-input" rows={2} value={form.description || ''} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
      </div>
      <div className="form-2col">
        <div className="form-row">
          <label className="form-label">Gate type</label>
          <select className="form-select" value={form.gate_type} onChange={(e) => setForm((p) => ({ ...p, gate_type: e.target.value }))}>
            {GATE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div className="form-row">
          <label className="form-label">Role</label>
          <select className="form-select" value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}>
            {Object.entries(DELIVERABLE_ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>
      <div className="form-2col">
        <div className="form-row">
          <label className="form-label">Order</label>
          <input className="form-input" type="number" min={1} value={form.order_index} onChange={(e) => setForm((p) => ({ ...p, order_index: Number(e.target.value) || 1 }))} />
        </div>
        <div className="form-row">
          <label className="form-label">Active</label>
          <select className="form-select" value={form.is_active ? 'yes' : 'no'} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.value === 'yes' }))}>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
    </Modal>
  );
}

export default function GateDeliverableTemplates() {
  const { profile } = useSupabaseAuth();
  const orgId = profile?.organisation_id;

  const [filterType, setFilterType] = useState('lifecycle');
  const [orgTemplates, setOrgTemplates] = useState([]);
  const [platformTemplates, setPlatformTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [orgRes, platRes] = await Promise.all([
        sb.from('org_gate_deliverable_templates')
          .select('*')
          .eq('organisation_id', orgId)
          .eq('gate_type', filterType)
          .order('order_index'),
        sb.from('gate_deliverable_templates')
          .select('*')
          .eq('gate_type', filterType)
          .eq('is_default', true)
          .order('order_index'),
      ]);
      setOrgTemplates(orgRes.data || []);
      setPlatformTemplates(platRes.data || []);
    } catch (e) {
      console.error('[GateDeliverableTemplates] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [orgId, filterType]);

  useEffect(() => { load(); }, [load]);

  async function saveTemplate(form) {
    if (!orgId) return;
    setSaving(true);
    setError(null);
    const payload = {
      organisation_id: orgId,
      name: form.name.trim(),
      description: form.description?.trim() || null,
      gate_type: form.gate_type,
      role: form.role,
      order_index: form.order_index || 1,
      is_active: form.is_active !== false,
    };
    const res = modal?.item
      ? await sb.from('org_gate_deliverable_templates').update(payload).eq('id', modal.item.id)
      : await sb.from('org_gate_deliverable_templates').insert(payload);
    setSaving(false);
    if (res.error) { setError(res.error.message); return; }
    setModal(null);
    load();
  }

  async function copyFromPlatform(tpl) {
    if (!orgId) return;
    setSaving(true);
    setError(null);
    const { error: insErr } = await sb.from('org_gate_deliverable_templates').insert({
      organisation_id: orgId,
      template_id: tpl.id,
      name: tpl.name,
      description: tpl.description,
      gate_type: tpl.gate_type,
      role: tpl.role,
      order_index: tpl.order_index,
      is_active: true,
    });
    setSaving(false);
    if (insErr) { setError(insErr.message); return; }
    load();
  }

  async function removeTemplate(id) {
    if (!window.confirm('Delete this org template?')) return;
    await sb.from('org_gate_deliverable_templates').delete().eq('id', id);
    load();
  }

  if (!orgId) {
    return <div className="content"><div className="empty">No organisation linked to your profile.</div></div>;
  }

  return (
    <>
      <div className="topbar">
        <div>
          <div className="tbtitle">Gate deliverable templates</div>
          <div className="tbcrumb">Org defaults seeded on stage gates (ADMIN — Phase 3b)</div>
        </div>
        <div className="badges">
          <button type="button" className="btn-primary" onClick={() => { setError(null); setModal({}); }}>+ Org template</button>
        </div>
      </div>

      <div className="content">
        <div className="filter-tabs" style={{ marginBottom: 16 }}>
          {GATE_TYPES.map((t) => (
            <button key={t.id} type="button" className={`ftab${filterType === t.id ? ' active' : ''}`} onClick={() => setFilterType(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? <Loader text="Loading templates…" /> : (
          <>
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="chdr"><span className="ctitle">Organisation templates</span></div>
              {orgTemplates.length === 0 ? (
                <div className="empty">No org templates for this gate type. Copy from platform defaults or add your own.</div>
              ) : (
                orgTemplates.map((t) => (
                  <div key={t.id} className="focus-row">
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{t.name}{!t.is_active && <span className="b rd" style={{ marginLeft: 8, fontSize: 9 }}>Inactive</span>}</div>
                      {t.description && <div style={{ fontSize: 11, color: '#888780', marginTop: 2 }}>{t.description}</div>}
                      <span className="b" style={{ fontSize: 9, marginTop: 4, display: 'inline-block' }}>{DELIVERABLE_ROLE_LABEL[t.role] || t.role}</span>
                    </div>
                    <button type="button" className="row-act" onClick={() => setModal({ item: t })}>Edit</button>
                    <button type="button" className="row-act" style={{ color: '#A32D2D' }} onClick={() => removeTemplate(t.id)}>Delete</button>
                  </div>
                ))
              )}
            </div>

            <div className="card">
              <div className="chdr"><span className="ctitle">Platform defaults (read-only)</span></div>
              {platformTemplates.length === 0 ? (
                <div className="empty">No platform defaults for this gate type.</div>
              ) : (
                platformTemplates.map((t) => (
                  <div key={t.id} className="focus-row">
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12 }}>{t.name}</div>
                      {t.description && <div style={{ fontSize: 11, color: '#888780', marginTop: 2 }}>{t.description}</div>}
                      <span className="b" style={{ fontSize: 9, marginTop: 4, display: 'inline-block' }}>{DELIVERABLE_ROLE_LABEL[t.role] || t.role}</span>
                    </div>
                    <button type="button" className="row-act" disabled={saving} onClick={() => copyFromPlatform(t)}>Copy to org</button>
                  </div>
                ))
              )}
            </div>
          </>
        )}
        {error && !modal && <div className="form-error" style={{ marginTop: 12 }}>{error}</div>}
      </div>

      {modal && (
        <TemplateModal
          item={modal.item}
          saving={saving}
          error={error}
          onClose={() => { setModal(null); setError(null); }}
          onSave={saveTemplate}
        />
      )}
    </>
  );
}
